import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormSection, SelectField } from '../components/FormComponents';
import { mockPRs } from '../data/mockData';
// Mock RFP/RFQ suppliers for a given PR
const mockPRSuppliers: Record<string, any[]> = {
  '1': [{
    id: '1',
    name: 'TechCorp Solutions',
    score: 87.5
  }, {
    id: '2',
    name: 'Global Logistics Inc',
    score: 80.0
  }],
  '2': [{
    id: '3',
    name: 'BuildIt Construction',
    score: 82.5
  }]
};
export function NegotiationCreate() {
  const navigate = useNavigate();
  const [selectedPR, setSelectedPR] = useState('');
  const [negotiationAmounts, setNegotiationAmounts] = useState<Record<string, string>>({});
  const [expandedSuppliers, setExpandedSuppliers] = useState<Record<string, boolean>>({});
  const approvedPRs = mockPRs.filter(pr => pr.status === 'Approved');
  const pr = approvedPRs.find(p => p.id === selectedPR);
  const availableSuppliers = selectedPR ? mockPRSuppliers[selectedPR] || [] : [];
  const handleAmountChange = (supplierId: string, itemId: string, amount: string) => {
    setNegotiationAmounts(prev => ({
      ...prev,
      [`${supplierId}-${itemId}`]: amount
    }));
  };
  const toggleSupplier = (supplierId: string) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };
  const handleSave = () => {
    if (!selectedPR) {
      alert('Please select a PR.');
      return;
    }
    alert('Negotiation saved successfully!');
    navigate('/negotiation');
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-1318">
      <div className="flex items-center justify-between" data-id="element-1319">
        <div className="flex items-center space-x-4" data-id="element-1320">
          <button onClick={() => navigate('/negotiation')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1321">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1322" />
          </button>
          <div data-id="element-1323">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-1324">
              Create Negotiation
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1325">
              Select a PR to record negotiation amounts for multiple suppliers
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1326">
          <button onClick={() => navigate('/negotiation')} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1327">
            Cancel
          </button>
          <button onClick={handleSave} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1328">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-1329" />
            Save Negotiation
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-8" data-id="element-1330">
        <FormSection title="Negotiation Context" data-id="element-1331">
          <SelectField label="Select Approved PR" value={selectedPR} onChange={e => {
          setSelectedPR(e.target.value);
          setNegotiationAmounts({});
          // Auto-expand all suppliers when a new PR is selected
          const suppliers = mockPRSuppliers[e.target.value] || [];
          const initialExpandedState: Record<string, boolean> = {};
          suppliers.forEach(s => {
            initialExpandedState[s.id] = true;
          });
          setExpandedSuppliers(initialExpandedState);
        }} options={[{
          value: '',
          label: 'Select a PR...'
        }, ...approvedPRs.map(p => ({
          value: p.id,
          label: `${p.prNo} - ${p.project} (${p.requisitionFor})`
        }))]} data-id="element-1332" />
        </FormSection>

        {pr && availableSuppliers.length > 0 && <div className="space-y-6" data-id="element-1333">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-1334">
              Supplier Negotiations
            </h3>

            {availableSuppliers.map(supplier => <div key={supplier.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1335">
                <button onClick={() => toggleSupplier(supplier.id)} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1336">
                  <div className="flex items-center space-x-3" data-id="element-1337">
                    <span className="font-semibold text-gray-900" data-id="element-1338">
                      {supplier.name}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1339">
                      Score: {supplier.score}
                    </span>
                  </div>
                  {expandedSuppliers[supplier.id] ? <ChevronUpIcon className="w-5 h-5 text-gray-500" data-id="element-1340" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1341" />}
                </button>

                {expandedSuppliers[supplier.id] && <div className="p-4 border-t border-gray-200" data-id="element-1342">
                    <div className="overflow-x-auto" data-id="element-1343">
                      <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1344">
                        <thead className="bg-gray-50" data-id="element-1345">
                          <tr data-id="element-1346">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1347">
                              Item
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1348">
                              Qty
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1349">
                              Rate
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1350">
                              Est. Cost
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-48" data-id="element-1351">
                              Negotiated Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1352">
                          {pr.lineItems.map(item => <tr key={item.id} data-id="element-1353">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1354">
                                {item.product}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1355">
                                {item.qty}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 text-right" data-id="element-1356">
                                ${item.rate.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right" data-id="element-1357">
                                ${item.estimatedCost.toLocaleString()}
                              </td>
                              <td className="px-4 py-3" data-id="element-1358">
                                <input type="number" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 text-right" value={negotiationAmounts[`${supplier.id}-${item.id}`] || ''} onChange={e => handleAmountChange(supplier.id, item.id, e.target.value)} placeholder="Enter amount" data-id="element-1359" />
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>}
              </div>)}
          </div>}

        {pr && availableSuppliers.length === 0 && <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-amber-800 text-sm" data-id="element-1360">
            No suppliers found from RFP/RFQ for this PR.
          </div>}
      </div>
    </div>;
}